<template>
  <div class="check-text-input">
    <div
      v-if="type !== 'textarea'"
      :class="editorInputWrapperClass"
      @mouseenter="isHoverStatus = true"
      @mouseleave="isHoverStatus = false"
    >
      <div
        ref="editor"
        class="editor"
        :placeholder="placeholder"
        :contenteditable="contentEditable"
        @input="onInputHandle"
        @blur="onBlurHandle"
        @focus="onFocusHandle"
        @keydown="onKeydownHandle"
        @paste="onPasteHandle"
        @compositionstart="onCompositionStartHandle"
        @compositionupdate="onCompositionUpdateHandle"
        @compositionend="onCompositionEndHandle"
        v-html="contentHtml"
      />
      <div class="editor-action">
        <div v-show="loading && !isFocusStatus">
          <i class="el-icon-loading icon-action"></i>
        </div>
        <div v-show="isFocusStatus && showClear">
          <i
            @mousedown.prevent
            @click="clear"
            class="el-icon-circle-close icon-action icon-action-close"
          ></i>
        </div>
      </div>
      <span
        v-if="isWordLimitVisible && type === 'text'"
        class="editor-input-count"
      >
        {{ textLength }}/{{ upperLimit }}
      </span>
    </div>
    <div
      v-else
      :class="editorTextareaWrapperClass"
      v-loading="loading && !isFocusStatus"
    >
      <div
        ref="editor"
        class="editor"
        :placeholder="placeholder"
        :contenteditable="contentEditable"
        @input="onInputHandle"
        @blur="onBlurHandle"
        @focus="onFocusHandle"
        @keydown="onKeydownHandle"
        @paste="onPasteHandle"
        @compositionstart="onCompositionStartHandle"
        @compositionupdate="onCompositionUpdateHandle"
        @compositionend="onCompositionEndHandle"
        v-html="contentHtml"
      />
      <span
        v-if="isWordLimitVisible && type === 'textarea'"
        class="editor-textarea-count"
      >
        {{ textLength }}/{{ upperLimit }}
      </span>
    </div>
    <el-input
      :readonly="readonly"
      :disabled="disabled"
      :rows="rows"
      :type="type"
      tabindex="-1"
    />
  </div>
</template>

<script>
import { checkTextSafe, checkTextSafeHubble } from './api.js'
import { debounce } from '@/utils'

export default {
  name: 'BLMCheckTextInput',
  props: {
    value: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'text',
    },
    rows: {
      type: Number,
      default: 2,
    },
    showWordLimit: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    maxlength: {
      type: [Number, String],
      default: 0,
    },
    request: {
      type: Function,
      default: () => {},
      required: true,
    },
  },
  data() {
    return {
      contentHtml: '',
      content: '',
      loading: false,
      isFocusStatus: false,
      isRequestContinue: true,
      /**
       * 组件是否Hover
       */
      isHoverStatus: false,
      /**
       * 是否在进行组合输入
       */
      isComposingStatus: false,
      formItemProp: '',
    }
  },
  computed: {
    isFirefox() {
      return navigator.userAgent.includes('Firefox')
    },
    contentEditable() {
      if (this.inputDisabled) {
        return false
      }
      if (this.isFirefox) {
        return true
      }
      return 'plaintext-only'
    },
    editorInputWrapperClass() {
      return this.inputDisabled
        ? 'editor-input-wrapper editor-wrapper-disabled'
        : 'editor-input-wrapper'
    },
    editorTextareaWrapperClass() {
      return this.inputDisabled
        ? 'editor-textarea-wrapper editor-wrapper-disabled'
        : 'editor-textarea-wrapper'
    },
    isHubble() {
      const isHubble = /\bhubble\b/gi.test(location.host)
      const hubbleTest = /\b3333\b/.test(location.host)
      return isHubble || hubbleTest
    },
    inputDisabled() {
      return this.disabled
    },
    /**
     * 组件的字数是否显示
     */
    isWordLimitVisible() {
      return (
        this.showWordLimit &&
        this.maxlength &&
        (this.type === 'text' || this.type === 'textarea') &&
        !this.inputDisabled &&
        !this.readonly
      )
    },
    upperLimit() {
      return this.maxlength
    },
    textLength() {
      if (typeof this.value === 'number') {
        return String(this.value).length
      }
      return (this.value || '').length
    },
    /**
     * 是否显示清除按钮
     */
    showClear() {
      return (
        this.clearable &&
        !this.inputDisabled &&
        !this.readonly &&
        (this.isFocusStatus || this.isHoverStatus)
      )
    },
  },
  created() {
    this.checkResLabelCategories = {
      normal: '正常文本',
      spam: '含垃圾信息',
      ad: '广告',
      politics: '政治',
      terrorism: '暴恐',
      abuse: '辱骂',
      porn: '色情',
      flood: '灌水',
      contraband: '违禁',
      meaningless: '无意义',
      customized: '自定义（比如命中自定义关键词）',
    }
  },
  mounted() {
    this.contentHtml = this.value
    this.setFormItemProp()
    this.initFormRules()
  },
  beforeDestroy() {
    this.removeFormRules()
  },
  watch: {
    value(val) {
      if (!this.isFocusStatus) {
        this.contentHtml = val
      }
    },
  },
  methods: {
    setFormItemProp() {
      const parent = this.deepParent(this.$parent)
      this.formItemProp = parent.prop
    },
    /**
     * composition 事件相关处理函数
     */
    onCompositionStartHandle(event) {
      this.$emit('compositionstart', event)
      this.isComposingStatus = true
    },
    onCompositionUpdateHandle(event) {
      this.$emit('compositionupdate', event)
    },
    onCompositionEndHandle(event) {
      this.$emit('compositionend', event)
      if (this.isComposingStatus) {
        this.isComposingStatus = false
        this.onInputHandle()
      }
    },
    /**
     * clear 事件的处理方法
     */
    clear() {
      if (this.$refs.editor) {
        this.$refs.editor.innerHTML = ''
        this.$emit('input', '')
        this.$emit('change', '')
        this.$emit('clear')
      }
    },
    /**
     * 修改当前输入框焦点的标志位
     */
    switchIsFocusStatusToTrue() {
      this.isFocusStatus = true
    },
    switchIsFocusStatusToFalse() {
      this.isFocusStatus = false
    },
    /**
     * 修改当前请求loading的标志位
     */
    switchLoadingStatusToTrue() {
      this.loading = true
    },
    switchLoadingStatusToFalse() {
      this.loading = false
    },
    /**
     * 组件CheckIng状态时标记是否取消了数据传输
     */
    switchRequestContinueStatusToTrue() {
      this.isRequestContinue = true
    },
    switchRequestContinueStatusToFalse() {
      this.isRequestContinue = false
    },
    /**
     * 处理粘贴的文本内容(如富文本)
     */
    onPasteHandle(event) {
      let paste = (event.clipboardData || window.clipboardData).getData('text')
      if (this.type !== 'textarea') paste = paste.replace(/[\r\n]/g, '')
      paste = this.onLimitEditorCountWhenPaste(paste)
      const selection = window.getSelection()
      if (!selection.rangeCount) return false
      selection.deleteFromDocument()
      selection.getRangeAt(0).insertNode(document.createTextNode(paste))
      selection.collapseToEnd()
      event.preventDefault()
      this.onInputHandle()
    },
    /**
     * 处理粘贴时超出字数限制的情况
     */
    onLimitEditorCountWhenPaste(text) {
      if (this.maxlength <= 0) return text
      if (text.length + this.textLength > this.maxlength) {
        text = text.slice(0, this.maxlength - this.textLength)
      }
      return text
    },
    /**
     * keyDown事件的处理函数
     */
    onKeydownHandle(event) {
      this.onLimitEditorCountWhenKeydown(event)
      if (this.type !== 'textarea' && event.code === 'Enter') {
        event.preventDefault()
        return false
      }
    },
    /**
     * keydown事件时关于字数限制的处理函数
     */
    onLimitEditorCountWhenKeydown(event) {
      if (this.maxlength <= 0) return
      const allowKey = [
        'Backspace',
        'Delete',
        'ArrowUp',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
      ]
      const isHotKeys = event.ctrlKey || event.metaKey || event.shiftKey
      if (
        this.textLength >= this.maxlength &&
        !isHotKeys &&
        !allowKey.includes(event.code)
      ) {
        event.preventDefault()
        return false
      }
    },
    /**
     * 处理给父级Form添加rules
     */
    initFormRules() {
      const parent = this.deepParent(this.$parent)
      if (
        parent.form.rules[parent.prop] &&
        parent.form.rules[parent.prop].some(
          (rule) => rule.name === 'BLMCheckTextInput',
        )
      ) {
        return
      }
      parent.form.rules[parent.prop].push({
        validator: async (rule, value, callback) => {
          try {
            callback(await this.checkText(value))
          } catch (error) {
            callback('服务或网络错误，请重试')
          }
        },
        name: 'BLMCheckTextInput',
        trigger: 'blur',
      })
    },
    removeFormRules() {
      const parent = this.deepParent(this.$parent)
      if (parent.form.rules && parent.form.rules[this.formItemProp]) {
        const index = parent.form.rules[this.formItemProp].findIndex(
          (rule) => rule.name === 'BLMCheckTextInput',
        )
        index > -1 && parent.form.rules[this.formItemProp].splice(index, 1)
      }
    },
    /**
     * 校验文本内容方法
     */
    validateTextContent() {
      this.initFormRules()
      this.content = this.formatContent(this.$refs.editor?.innerHTML || '')
      if (!this.debounceValidateField) {
        this.debounceValidateField = debounce(this.validateField)
      }
      this.debounceValidateField()
    },
    validateField() {
      const parent = this.deepParent(this.$parent)
      parent.form.validateField(parent.prop)
    },
    /**
     * input 处理事件
     */
    onInputHandle() {
      /**
       * should not emit input during composition
       * see: https://github.com/ElemeFE/element/issues/10516
       * @2022-11-30
       *  */
      if (this.isComposingStatus) return
      this.content = this.$refs.editor?.innerText.replace('\n\n', '\n')
      this.$emit('input', this.content)
    },
    onFocusHandle() {
      this.switchIsFocusStatusToTrue()
      this.$emit('focus')
    },
    async onBlurHandle() {
      this.switchIsFocusStatusToFalse()
      this.validateTextContent()
      this.$emit('blur')
    },
    /**
     * 校验文本内容是否违规
     */
    async checkText(value) {
      try {
        this.switchLoadingStatusToTrue()
        this.switchRequestContinueStatusToTrue()
        const privateCheckTextSafe = this.isHubble
          ? checkTextSafeHubble
          : checkTextSafe
        const checkRes = await privateCheckTextSafe(this.request, {
          // contents: [value]
          content: value,
        })
        this.switchLoadingStatusToFalse()
        /**
         * 异步check过程中可能会终止check
         */
        // if (this.isFocusStatus || !this.isRequestContinue) return;
        if (
          checkRes.data &&
          checkRes.data.suggestion === 'block'
        ) {
          this.formatContentHtml(checkRes.data.contexts)
          return `配置信息涉嫌“${
            this.checkResLabelCategories[checkRes.data.label]
          }”，请重新配置`
        }
        /**
         * 如果不违规我们需要重置文本内容(删除红色标记)
         */
        this.formatContentHtml()
        /**
         * 文本内容安全事件检测通过时触发
         */
        this.$nextTick(() => {
          this.$emit('validatePassed')
        })
      } catch (error) {
        this.switchLoadingStatusToFalse()
      }
    },
    /**
     * 把innerHtml内容转换成纯文本
     */
    formatContent(html = '') {
      const content = html.replace(/<.+?>/g, '')
      return content
    },
    /**
     * 把文本内容根据违规字段转换成html
     */
    formatContentHtml(contexts = []) {
      let content = this.formatContent(this.$refs.editor?.innerHTML)
      let keys
      const replaceText = '#$#'

      for (let { context } of contexts) {
        context = context.replace(/~/g, replaceText)
        context = context.replace(/&/g, replaceText)
        keys = context.split(replaceText)
        for (const key of keys) {
          content = content.replace(
            new RegExp(key, 'g'),
            `<span style="color: red;" contenteditable='false'>${key}</span>`,
          )
        }
      }
      if (this.$refs.editor && this.$refs.editor.innerHTML) {
        this.$refs.editor.innerHTML = content
      }
    },
    /**
     * 向上查找form父级(控制表单状态)
     */
    deepParent(parent) {
      if (parent) {
        if (parent.form) {
          return parent
        } else if (parent.$parent) {
          return this.deepParent(parent.$parent)
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.check-text-input {
  position: relative;
  overflow: hidden;
  cursor: text;
  .editor-input-wrapper {
    position: absolute;
    z-index: 10;
    padding: 0 12px;
    display: flex;
    width: 100%;
    .editor {
      max-width: 100%;
      white-space: nowrap;
      overflow-x: auto;
      flex: 1;
    }
    .editor:empty:before {
      content: attr(placeholder);
      color: #bcbdc0;
      pointer-events: none;
    }
    .editor:focus:before {
      content: none;
    }
    .editor-action {
      font-size: 12px;
      .icon-action-close {
        color: #bbbdc1;
        cursor: pointer;
      }
      .icon-action-close:hover {
        color: #848587;
      }
    }
    .icon-action {
      margin-left: 2px;
    }
    .editor-input-count {
      margin-left: 5px;
      color: #323233;
      font-size: 12px;
      user-select: none;
      pointer-events: none;
    }
  }
  .editor-textarea-wrapper {
    position: absolute;
    z-index: 10;
    padding: 6px 12px;
    width: 100%;
    height: 100%;
    .editor {
      line-height: 1.5;
      max-width: 100%;
      height: 100%;
      overflow-y: auto;
      word-wrap: break-word;
      word-break: break-all;
    }
    .editor:empty:before {
      content: attr(placeholder);
      color: #bcbdc0;
      pointer-events: none;
    }
    .editor:focus:before {
      content: none;
    }
    .editor-textarea-count {
      color: #323233;
      background: #fff;
      font-size: 12px;
      position: absolute;
      bottom: 5px;
      right: 10px;
      user-select: none;
      pointer-events: none;
    }
  }
  .editor-wrapper-disabled {
    color: #c8c9cc;
  }
}
</style>
